import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import purpleIcon from "../../assets/images/user-group-purple.png";
import redIcon from "../../assets/images/user-group-red.png";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import DocumentPopup from "../../components/Reusable/DocumentPopup";
import formatDate from "../../components/Reusable/dateUtils";
import DownloadLeaveDetails from "./DownloadLeaveDetails";

const getManagerStatusBadge = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized.includes("approve")) {
    return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  }
  if (normalized.includes("reject") || normalized.includes("declin")) {
    return "bg-red-50 text-red-700 ring-red-200";
  }
  return "bg-amber-50 text-amber-700 ring-amber-200";
};

const EmployeeLeaveMoreDetails = ({
  setShowEmployeeLeaveDetails,
  employeeData,
}) => {
  const accessToken = localStorage.getItem("accessToken");
  const [showDocumentPopup, setShowDocumentPopup] = useState(false);
  const [leaveCount, setLeaveCount] = useState([]);

  const fetchLeaveCount = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/employees/${employeeData.user?.employee_information_id}/leaveCount`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const data = response.data?.data || [];
      setLeaveCount(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log(error);
      setLeaveCount([]);
    }
  };

  useEffect(() => {
    fetchLeaveCount();
  }, []);

  const employeeInfoId = employeeData?.user?.employee_information_id;
  const empCountry = employeeData?.user?.roles[0]?.company[0]?.country;

  const leaveTypes = [
    ...new Set(leaveCount.map((leave) => leave?.LeaveType).filter(Boolean)),
  ];

  const totalLeaves = Number(
    leaveCount
      .filter(
        (leave) =>
          leave?.country === empCountry &&
          leaveTypes.includes(leave?.LeaveType),
      )
      .reduce((total, leave) => total + (parseFloat(leave?.count) || 0), 0)
      .toFixed(2),
  );

  const totalLeavesTaken = leaveCount
    .filter(
      (leave) =>
        leave?.country === empCountry && leaveTypes.includes(leave?.LeaveType),
    )
    .reduce(
      (total, leave) => total + (parseFloat(leave?.leave_days_taken) || 0),
      0,
    )
    .toFixed(2);

  const handleOpenDocumentPopup = () => {
    setShowDocumentPopup(true);
  };

  console.log("Employee Data", employeeData);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div className="relative w-full max-w-5xl mx-4 h-[95vh] flex flex-col bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 overflow-hidden">
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-8 py-3 border-b border-gray-300 bg-white">
          <div>
            <h3 className="text-xl font-semibold text-charcoal tracking-tight">
              Employee Leave Details
            </h3>
            <p className="text-sm text-muted mt-0.5">
              Full breakdown of this leave request
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowEmployeeLeaveDetails(null)}
            className="flex items-center justify-center h-8 w-8 rounded-full text-desc-gray hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <span className="text-lg leading-none">&times;</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
          {/* Profile + leave stats */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
                src={employeeData?.user?.profile_picture}
                alt="Employee"
                className="w-16 h-16 rounded-full object-cover ring-2 ring-offset-2 ring-[var(--primary-color)]/30"
              />
              <div>
                <h4 className="text-base font-semibold m-0 text-medium-dark-gray">
                  {employeeData?.user?.employee_full_name}
                </h4>
                <p className="text-sm font-normal m-0 text-muted">
                  {employeeData?.user?.roles[0]?.role_name}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="relative group flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 w-[210px] shadow-sm hover:shadow-md hover:border-gray-300 transition-all cursor-default">
                <div className="flex items-center justify-center shrink-0 w-11 h-11 rounded-full bg-[#f4f3ff]">
                  <img
                    src={purpleIcon}
                    alt=""
                    className="w-5 h-5 object-contain"
                  />
                </div>
                <div>
                  <p className="m-0 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    Total Available
                  </p>
                  <p className="m-0 mt-0.5 text-xl font-bold text-gray-900">
                    {totalLeaves}
                    <span className="ml-1 text-[11px] font-medium text-gray-400">
                      / yearly
                    </span>
                  </p>
                </div>

                <ul className="hidden group-hover:flex flex-col absolute top-full py-2 gap-1 right-0 mt-2 w-56 rounded-xl border border-gray-100 bg-primary-hrms shadow-lg divide-y divide-gray-100 z-20 overflow-hidden">
                  {leaveCount
                    .filter((leave) => leave?.country === empCountry)
                    .map((leave, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between px-3.5 py-1 text-xs text-white hover:bg-gray-50"
                      >
                        <span>{leave?.LeaveType}</span>
                        <span className="font-semibold text-[#ECFDF5]">
                          {(parseFloat(leave?.count) || 0).toFixed(2)}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>

              <div className="relative group flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 w-[210px] shadow-sm hover:shadow-md hover:border-gray-300 transition-all cursor-default">
                <div className="flex items-center justify-center shrink-0 w-11 h-11 rounded-full bg-[#fff3f3]">
                  <img
                    src={redIcon}
                    alt=""
                    className="w-5 h-5 object-contain"
                  />
                </div>
                <div>
                  <p className="m-0 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    Leaves Taken
                  </p>
                  <p className="m-0 mt-0.5 text-xl font-bold text-gray-900">
                    {totalLeavesTaken}
                    <span className="ml-1 text-[11px] font-medium text-gray-400">
                      / yearly
                    </span>
                  </p>
                </div>

                <ul className="hidden group-hover:flex flex-col absolute top-full py-2 gap-1 right-0 mt-2 w-56 rounded-xl border border-gray-100 bg-primary-hrms shadow-lg divide-y divide-gray-100 z-20 overflow-hidden">
                  {leaveCount
                    .filter((leave) => leave?.country === empCountry)
                    .map((leave, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between px-3.5 py-1 text-xs text-white hover:bg-gray-50"
                      >
                        <span>{leave?.LeaveType}</span>
                        <span className="font-semibold text-[#ECFDF5]">
                          {(parseFloat(leave?.leave_days_taken) || 0).toFixed(
                            2,
                          )}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Employee ID / Applied date / Download / Leave history */}
          <div className="flex items-center justify-between rounded-xl bg-gray-50 px-5 py-3">
            <div className="flex items-center gap-5 text-sm">
              <div>
                <span className="text-gray-400">Employee ID</span>
                <span className="ml-1.5 font-semibold text-gray-700">
                  {employeeData?.user?.employee_id}
                </span>
              </div>
              <div className="h-4 w-px bg-gray-200" />
              <div>
                <span className="text-gray-400">Applied on</span>
                <span className="ml-1.5 font-semibold text-gray-700">
                  {formatDate(employeeData?.apply_date)}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-5">
              <DownloadLeaveDetails
                employeeData={employeeData}
                leaveCount={leaveCount}
                empCountry={empCountry}
              />
              {employeeInfoId && (
                <Link
                  to={`/employeeleavehistory/${employeeInfoId}`}
                  className="inline-flex items-center gap-1 text-sm font-medium text-[var(--secondary-color)] hover:underline"
                >
                  <MdOutlineRemoveRedEye className="text-base" />
                  View Leave History
                </Link>
              )}
            </div>
          </div>

          {/* Leave details grid */}
          <div className="grid grid-cols-5 gap-4 rounded-xl border border-gray-100 bg-[#f9fafe] px-5 py-4">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                Leave Start Date
              </p>
              <p className="mt-1 text-sm font-semibold text-gray-800">
                {formatDate(employeeData?.leave_from)}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                Leave End Date
              </p>
              <p className="mt-1 text-sm font-semibold text-gray-800">
                {formatDate(employeeData?.leave_to)}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                Leave Type
              </p>
              <p className="mt-1 text-sm font-semibold text-gray-800">
                {employeeData?.leave_type}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                No. of Days
              </p>
              <p className="mt-1 text-sm font-semibold text-gray-800">
                {employeeData?.days_count}
              </p>
            </div>
            {employeeData.document_url && (
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                  Document
                </p>
                <button
                  type="button"
                  onClick={handleOpenDocumentPopup}
                  className="mt-1 inline-flex items-center rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 active:scale-[0.98] transition"
                >
                  View document
                </button>
              </div>
            )}
          </div>

          {/* Reason */}
          <div className="rounded-xl border border-gray-100 bg-gray-50 px-5 py-4">
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              Reason for leave
            </p>
            <p className="mt-1.5 text-sm text-gray-700 leading-relaxed">
              {employeeData?.purpose}
            </p>
          </div>

          {/* Approving managers */}
          <div>
            <p className="mb-2 text-sm font-semibold text-gray-700">
              Approving Managers
            </p>
            <div className="overflow-hidden rounded-xl border border-gray-100">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      ID
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      Name
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      Designation
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {employeeData?.approving_managers_status?.map(
                    (manager, index) => (
                      <tr
                        key={manager?.emp_id ?? index}
                        className="hover:bg-gray-50/60"
                      >
                        <td className="px-4 py-2.5 text-gray-600">
                          {manager?.emp_id}
                        </td>
                        <td className="px-4 py-2.5 font-medium text-gray-800">
                          {manager?.manager_name}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {manager?.role_name}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${getManagerStatusBadge(manager?.status)}`}
                          >
                            {manager?.status}
                          </span>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50">
                    <td
                      colSpan="4"
                      className="px-4 py-2.5 text-xs font-medium text-gray-500"
                    >
                      Total Approvers: {employeeData?.approving_manager_count}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Remarks */}
          <div className="rounded-xl border border-gray-100 bg-gray-50 px-5 py-4">
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              Remarks / Notes
            </p>
            <p className="mt-1.5 text-sm text-gray-700 leading-relaxed">
              {employeeData?.deny_reason}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 flex items-center justify-end px-8 py-3 border-t border-gray-100 bg-white">
          <button
            type="button"
            onClick={() => setShowEmployeeLeaveDetails(null)}
            className="rounded-full px-6 h-10 text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-[0.98] transition"
          >
            Close
          </button>
        </div>

        {showDocumentPopup && (
          <DocumentPopup
            documentUrl={employeeData.document_url}
            onClose={() => setShowDocumentPopup(false)}
          />
        )}
      </div>
    </div>
  );
};

export default EmployeeLeaveMoreDetails;
